<template>
    <v-form ref="form" @keyup.native.enter="submit">
      <validation-observer ref="observer" v-slot="{ invalid }">
        <v-row justify="center">
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('Headers.first_name')"
              rules="required|name_validate"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('Headers.first_name')"
                prepend-inner-icon="mdi-pencil"
                v-model="user.first_name"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('Headers.last_name')"
              rules="required|name_validate"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('Headers.last_name')"
                prepend-inner-icon="mdi-pencil"
                v-model="user.last_name"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              :name="$t('Headers.role')"
              rules="required|text"
            >
              <v-select
                :rules="[rules.required]"
                :label="$t('AdminPage.Role')"
                :items="roles"
                filled
                v-model="user.account_role"
                required
                prepend-inner-icon="mdi-pencil"
              ></v-select>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('Headers.email')"
              rules="required|email"
            >
              <v-text-field
                class="text-input"
                filled
                :rules="[rules.required]"
                :label="$t('Headers.email')"
                prepend-inner-icon="mdi-pencil"
                v-model="user.email"
                required
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <vue-tel-input-vuetify
              filled
              v-model="user.phone"
              @input="checkPhone"
              defaultCountry="DE"
              :placeholder="$t('Headers.phone')"
              :label="$t('Headers.phone')"
              :error-messages="errorsPhone"
              mode="international"
              data-cy="contactPhone"
            />
          </v-col>
          
        </v-row>
        <v-row>
          <v-col class="text-left" cols="12" md="3" lg="3" sm="3">
            <v-checkbox
              :color="color"
              v-model="changePassword"
              :label="$t('AdminPage.ChangePassword')"
              @click="
                (newPassword = undefined), (confirmNewPassword = undefined)
              "
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.NewPassword')"
              rules="password"
            >
              <v-text-field
                filled
                v-if="changePassword"
                :label="$t('AdminPage.NewPassword')"
                v-model="newPassword"
                :hint="$t('RegisterPage.form_info_3')"
                :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="() => (showNewPassword = !showNewPassword)"
                :type="showNewPassword ? 'text' : 'password'"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.ConfirmNewPassword')"
              rules="password"
            >
              <v-text-field
                filled
                v-if="changePassword"
                :label="$t('AdminPage.ConfirmNewPassword')"
                v-model="confirmNewPassword"
                :append-icon="
                  showNewPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'
                "
                @click:append="
                  () => (showNewPasswordConfirm = !showNewPasswordConfirm)
                "
                :type="showNewPasswordConfirm ? 'text' : 'password'"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
          <v-col cols="6" sm="4">
            <validation-provider
              v-slot="{ errors }"
              :name="$t('AdminPage.Password')"
              rules="required"
            >
              <v-text-field
                filled
                v-model="oldPassword"
                :label="$t('AdminPage.Password')"
                :hint="$t('AdminPage.PasswordSubmit')"
                persistent-hint
                :append-icon="showOldPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="() => (showOldPassword = !showOldPassword)"
                :type="showOldPassword ? 'text' : 'password'"
                :error-messages="errors"
              ></v-text-field>
            </validation-provider>
          </v-col>
        </v-row>
        <v-row justify="center" class="mx-n5">
          <v-col class="text-right">
            <custom-button
              :disabled="invalid"
              @click="submit"
              :text="$t('Headers.save')"
            ></custom-button>
          </v-col>
        </v-row>
      </validation-observer>
    </v-form>
</template>

<script>
import i18n from "@/i18n";
import CustomButton from "../../components/ui/input/CustomButton";
import {
  extend,
  ValidationProvider,
  setInteractionMode,
  ValidationObserver,
} from "vee-validate";
import { required, email } from "vee-validate/dist/rules";

import { log } from "@/helpers/logger";

setInteractionMode("eager");

extend("required", {
  ...required,
  message: i18n.t("FormValidation.required"),
});

extend("email", {
  ...email,
  message: i18n.t("FormValidation.email"),
});

extend("name_validate", {
  validate: (value) =>
    /(^[a-zA-ZäÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.\s]+)$/.test(
      value
    ),
  message: i18n.t("FormValidation.name"),
});

extend("text", {
  validate: (value) =>
    /(^[a-zA-Z0-9äÄöÖüÜßàÀèÈìÌòÒùÙçÇãÃñÑõÕëËïÏâÂêÊîÎôÔûÛáÁéÉíÍóÓúÚýÝ\-.&\s]+)$/.test(
      value
    ),
  message: i18n.t("FormValidation.field"),
});

export default {
  name: "account",
  components: {
    CustomButton,
    ValidationProvider,
    ValidationObserver,
  },
  title: i18n.t("HomePage.header.admin"),
  props: {
    title: String,
  },
  data() {
    return {
      user: {},
      user_reset: {},
      licenses: [],
      roles: [],
      changePassword: false,
      showNewPassword: false,
      showNewPasswordConfirm: false,
      showOldPassword: false,
      errorsPhone: "",
      oldPassword: undefined,
      newPassword: undefined,
      confirmNewPassword: undefined,
      color: "",
      rules: {
        required: (value) => !!value || this.$t("Rules.required"),
      },
      roles: [
        {
          value: "admin",
          text: this.$t("AdminPage.AdminRole"),
        },
        {
          value: "user",
          text: this.$t("AdminPage.UserRole"),
        },
      ],
      language_items: [
        {
          value: "en",
          text: "EN",
        },
        {
          value: "de",
          text: "DE",
        },
      ],
    };
  },
  created() {
    this.$store.commit("set_loading", true);
    this.color = this.$vuetify.theme.themes.light.primary;
    this.user = this.$store.state.auth.user;
    this.user_reset = this.user;
    this.$store.commit("set_loading", false);
  },
  methods: {
    submit() {
      this.$store.commit("set_loading", true);
      if (this.user.first_name) {
        if (!this.$refs.form.validate()) {
          this.$emit("close");
          this.$store.commit("set_loading", false);
          return false;
        }
        this.user.password = this.oldPassword;
        this.user.newPassword = this.newPassword;
        this.user.confirmNewPassword = this.confirmNewPassword;
        this.$store
          .dispatch("data/updateAccountAdministrator", this.user)
          .then(() => {
            this.$store.commit("auth/set_user", this.user);
            log(
              "AdminPage.Account.submit() - Update Account Administrator " +
                this.user.id,
              "info"
            );
            location.reload();
          })
          .catch((err) => {
            this.$emit("close");
            log("AdminPage.Account.submit() - " + err, "error");
          });
      }
    },
    checkPhone(number, phone) {
      if (phone.isValid) {
        if (phone.type != "mobile") {
          this.errorsPhone = i18n.t("FormValidation.mobile");
          this.$store.commit("set_loading", false);
          this.$emit("checked", false);
          return false;
        }
        this.errorsPhone = "";
        this.phone = phone.number.e164;
        this.$emit("checked", true);
        return true;
      } else {
        this.errorsPhone = i18n.t("FormValidation.phone");
        this.$store.commit("set_loading", false);
        this.$emit("checked", false);
        return false;
      }
    },
    close() {
      this.$emit("close");
    },
    setLocale(locale) {
      localStorage.setItem("language", JSON.stringify(locale));
      this.$store.commit("auth/set_language", locale);
      this.$router.go();
    },
  },
};
</script>
